import { readSettingsFile, writeSettingsFile } from './settingsFile';
import { isAppLanguage, setUiLanguage, t } from './language';
import { isSummaryTemplateId } from './summaryTemplates';
import type {
  AppLanguage,
  AppTheme,
  BehaviorPrefs,
  MeetingPrefs,
  SummaryLanguage,
  SummaryStylePrefs,
  TranscribeLanguage,
} from '../shared/types';

export interface AppSettings {
  /** Yeni kayıtların varsayılan dili. Kayıt ekranından kayıt başına değiştirilebilir. */
  transcribeLanguage: TranscribeLanguage;
  /** Takvimde kaç gün ileriye bakılacağı. */
  calendarLookaheadDays: number;
  /** Kayıt bitince özet e-postası otomatik gönderilsin mi. */
  summaryEmailEnabled: boolean;
  /**
   * Özetin gideceği adres. Boşsa Google hesabının kendi adresi kullanılır —
   * yani varsayılan davranış "kendine gönder".
   */
  /**
   * Genel ayar: takvimdeki Meet/Zoom/Teams toplantılarına bot otomatik
   * gönderilsin mi. Yalnızca elle dokunulmamış toplantılara uygulanır.
   */
  autoJoinBot: boolean;
  /** Bot düğmesi elle kapatılan takvim etkinlikleri (genel ayar açık olsa da bot gitmez). */
  autoJoinExcluded: string[];
  /** Bot düğmesi elle açılan takvim etkinlikleri (genel ayar kapalı olsa da bot gider). */
  autoJoinIncluded: string[];
  /** Arayüz dili (Ayarlar > Genel). Cihaza özel; giriş ekranı da bu dilde açılır. */
  uiLanguage: AppLanguage;
  /** Yapay zekâ özetlerinin dili. Varsayılan 'auto': konuşmanın dili. */
  summaryLanguage: SummaryLanguage;
  /** Özet stili: kullanıcının kuralları ve seçili şablon. */
  summaryStyle: SummaryStylePrefs;
  /** Transkriptte doğru yazılması istenen isimler/terimler (Deepgram keyterm). */
  transcriptTerms: string[];
  /** Tema tercihi; 'system' işletim sistemini izler. */
  theme: AppTheme;
  /** Davranış kartındaki anahtarlar (kısayollar, sesler, pencere). */
  behavior: BehaviorPrefs;
  /** Takvim > Toplantılar: hatırlatıcı, görüşme algılama, otomatik durdurma. */
  meetings: MeetingPrefs;
  /** Özet, toplantıdaki herkese (takvim katılımcılarına) de gönderilsin mi. */
  summaryEmailEveryone: boolean;
  /** Etkinlikleri okunan Google takvimleri. Boş = yalnızca ana takvim. */
  googleCalendarIds: string[];
  /** Bu bilgisayarda ilk kurulumu (onboarding) bitirmiş hesaplar. */
  onboardedUsers: string[];
  /**
   * Hesap başına son onaylanan metin sürümleri — asıl kayıt sunucuda
   * (user_consents); bu kopya yalnızca sunucuya ulaşılamadığında uygulamanın
   * açılabilmesi için (bkz. onboarding.ts).
   */
  legalAccepted: LegalAcceptance[];
}

export interface LegalAcceptance {
  userId: string;
  versions: Record<string, string>;
}

/** Ayarlar ekranının yazabildiği kişisel tercihler. */
export type AppPrefs = Pick<
  AppSettings,
  'summaryLanguage' | 'summaryStyle' | 'transcriptTerms' | 'theme' | 'behavior' | 'meetings'
>;

/** Terim sayısı ve uzunluğu sınırlı: istek adresi ve istem şişmesin. */
export const MAX_TRANSCRIPT_TERMS = 100;
export const MAX_TRANSCRIPT_TERM_LENGTH = 80;

/** Boşlukları sadeleştirir, tekrarları (büyük/küçük harf duyarsız) atar, sayıyı sınırlar. */
export function sanitizeTranscriptTerms(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const term = raw.replace(/\s+/g, ' ').trim().slice(0, MAX_TRANSCRIPT_TERM_LENGTH);
    if (!term) continue;
    const key = term.toLocaleLowerCase('tr');
    if (seen.has(key)) continue;
    seen.add(key);
    terms.push(term);
    if (terms.length >= MAX_TRANSCRIPT_TERMS) break;
  }
  return terms;
}

/** Kural sayısı ve uzunluğu sınırlı: istem şişmesin. */
export const MAX_SUMMARY_RULES = 10;
export const MAX_SUMMARY_RULE_LENGTH = 200;

const DEFAULT_SUMMARY_STYLE: SummaryStylePrefs = { rules: [], templateId: 'general' };

/** Kuralları temizler: boşlar atılır, uzunluk ve sayı sınırlanır. */
export function sanitizeSummaryStyle(value: unknown): SummaryStylePrefs {
  const input = (value ?? {}) as Partial<SummaryStylePrefs>;
  const rules = Array.isArray(input.rules)
    ? input.rules
        .filter((rule): rule is string => typeof rule === 'string')
        .map((rule) => rule.replace(/\s+/g, ' ').trim().slice(0, MAX_SUMMARY_RULE_LENGTH))
        .filter(Boolean)
        .slice(0, MAX_SUMMARY_RULES)
    : [];
  return {
    rules,
    templateId: isSummaryTemplateId(input.templateId) ? input.templateId : DEFAULT_SUMMARY_STYLE.templateId,
  };
}

const DEFAULT_BEHAVIOR: BehaviorPrefs = {
  // Varsayılan kapalı: kullanıcı istemeden uygulama açılışa eklenmez.
  launchAtLogin: false,
  recordingSounds: true,
  recordingShortcut: true,
  recordingNotesShortcut: true,
  quickFindShortcut: true,
  // Varsayılan kapalı: pencere normal, paylaşılabilir bir pencere olarak kalsın.
  hideFromScreenShare: false,
};

const DEFAULT_MEETINGS: MeetingPrefs = {
  calendarReminders: true,
  detectCalls: true,
  // Bugünkü davranış: görüşme bitince geri sayımlı uyarı.
  autoStopOnCallEnd: true,
};

/** Seçilebilecek Google takvimi sayısı — her biri ayrı bir istek demek. */
export const MAX_GOOGLE_CALENDARS = 20;

/** Takvim kimliklerini temizler: boşlar ve tekrarlar atılır, sayı sınırlanır. */
export function sanitizeCalendarIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const id = raw.trim();
    if (!id || id.length > 256 || ids.includes(id)) continue;
    ids.push(id);
    if (ids.length >= MAX_GOOGLE_CALENDARS) break;
  }
  return ids;
}

/**
 * Kayıt ekranının okuduğu alt küme — yönetici kapısı olmadan servis edilir.
 * Buraya hassas bir alan eklenmemeli (bkz. ipc/register.ts).
 */
export type RecordingPrefs = Pick<AppSettings, 'transcribeLanguage'>;

/**
 * Özet e-postası tercihleri. Kayıt tercihleri gibi bunlar da yönetici
 * kapısının DIŞINDA servis ediliyor: kullanıcının kendi e-posta adresi
 * kişisel bir tercih, yönetici ayarı değil — aksi hâlde normal bir kullanıcı
 * kendi özetinin nereye gittiğini ne görebilir ne değiştirebilirdi.
 */
export type EmailPrefs = Pick<AppSettings, 'summaryEmailEnabled' | 'summaryEmailEveryone'>;

/** Otomatik bot katılımı — kişisel tercih, yönetici kapısı dışında. */
export type AutoJoinPrefs = Pick<AppSettings, 'autoJoinBot' | 'autoJoinExcluded' | 'autoJoinIncluded'>;

/** Hariç tutma / dahil etme listelerinin üst sınırı — eski etkinlik kimlikleri sonsuza kadar birikmesin. */
const MAX_AUTO_JOIN_EVENTS = 300;

function sanitizeEventIds(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input
    .filter((id): id is string => typeof id === 'string' && id.length > 0 && id.length < 512)
    .slice(-MAX_AUTO_JOIN_EVENTS);
}

const DEFAULTS: AppSettings = {
  transcribeLanguage: 'tr',
  calendarLookaheadDays: 7,
  // Varsayılan açık: özet kullanıcının KENDİ adresine gidiyor, dışarıya
  // hiçbir veri paylaşılmıyor.
  summaryEmailEnabled: true,
  // Varsayılan kapalı: bot, kullanıcı açıkça istemeden toplantılara girmemeli.
  autoJoinBot: false,
  autoJoinExcluded: [],
  autoJoinIncluded: [],
  uiLanguage: 'tr',
  summaryLanguage: 'auto',
  summaryStyle: DEFAULT_SUMMARY_STYLE,
  transcriptTerms: [],
  theme: 'system',
  behavior: DEFAULT_BEHAVIOR,
  meetings: DEFAULT_MEETINGS,
  // Varsayılan kapalı: özet kullanıcı açıkça istemeden başkalarına gitmez.
  summaryEmailEveryone: false,
  googleCalendarIds: [],
  onboardedUsers: [],
  legalAccepted: [],
};

export function isSummaryLanguage(value: unknown): value is SummaryLanguage {
  return value === 'auto' || value === 'tr' || value === 'en';
}

export function isAppTheme(value: unknown): value is AppTheme {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Eksik/bozuk alanlar varsayılana düşer — ayar dosyası elle düzenlenebiliyor. */
function sanitizeBehavior(value: unknown): BehaviorPrefs {
  const input = (value ?? {}) as Partial<BehaviorPrefs>;
  const flag = (key: keyof BehaviorPrefs) =>
    typeof input[key] === 'boolean' ? (input[key] as boolean) : DEFAULT_BEHAVIOR[key];
  return {
    launchAtLogin: flag('launchAtLogin'),
    recordingSounds: flag('recordingSounds'),
    recordingShortcut: flag('recordingShortcut'),
    recordingNotesShortcut: flag('recordingNotesShortcut'),
    quickFindShortcut: flag('quickFindShortcut'),
    hideFromScreenShare: flag('hideFromScreenShare'),
  };
}

function sanitizeMeetings(value: unknown): MeetingPrefs {
  const input = (value ?? {}) as Partial<MeetingPrefs>;
  const flag = (key: keyof MeetingPrefs) =>
    typeof input[key] === 'boolean' ? (input[key] as boolean) : DEFAULT_MEETINGS[key];
  return {
    calendarReminders: flag('calendarReminders'),
    detectCalls: flag('detectCalls'),
    autoStopOnCallEnd: flag('autoStopOnCallEnd'),
  };
}

/**
 * Dil doğrulaması. `Record<TranscribeLanguage, true>` olarak yazıldığı için
 * birliğe yeni bir dil eklendiğinde burası derlenmez — liste sessizce eskimez.
 */
const SUPPORTED_LANGUAGES: Record<TranscribeLanguage, true> = { auto: true, tr: true, en: true };

export function isTranscribeLanguage(value: unknown): value is TranscribeLanguage {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SUPPORTED_LANGUAGES, value);
}

let cache: AppSettings | null = null;

export async function getSettings(): Promise<AppSettings> {
  if (cache) return cache;

  const stored = await readSettingsFile();
  cache = stored ? sanitize(stored) : { ...DEFAULTS };
  setUiLanguage(cache.uiLanguage);
  return cache;
}

type SettingsListener = (settings: AppSettings) => void;
const savedListeners = new Set<SettingsListener>();

/** Ayarlar her kaydedildiğinde çağrılır (ör. özet tercihlerinin sunucu kopyası — prefsSync.ts). */
export function onSettingsSaved(listener: SettingsListener): () => void {
  savedListeners.add(listener);
  return () => savedListeners.delete(listener);
}

export async function saveSettings(patch: unknown): Promise<AppSettings> {
  const current = await getSettings();
  const next = sanitize({ ...current, ...(patch as object) });

  await writeSettingsFile(next);

  cache = next;
  setUiLanguage(next.uiLanguage);
  for (const listener of savedListeners) {
    try {
      listener(next);
    } catch (err) {
      console.error('[settings] dinleyici hatası:', err);
    }
  }
  return next;
}

/**
 * Gelen değerleri sınırlara sıkıştırır. Ayarlar dosyası elle düzenlenebilir
 * bir JSON — oradan gelen 0 saniyelik parça uzunluğu sonsuz döngü demek.
 */
function sanitize(value: unknown): AppSettings {
  const input = (value ?? {}) as Partial<AppSettings>;
  const clamp = (n: unknown, min: number, max: number, fallback: number) =>
    typeof n === 'number' && Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback;

  return {
    transcribeLanguage: isTranscribeLanguage(input.transcribeLanguage)
      ? input.transcribeLanguage
      : DEFAULTS.transcribeLanguage,
    calendarLookaheadDays: clamp(input.calendarLookaheadDays, 1, 30, DEFAULTS.calendarLookaheadDays),
    summaryEmailEnabled:
      typeof input.summaryEmailEnabled === 'boolean'
        ? input.summaryEmailEnabled
        : DEFAULTS.summaryEmailEnabled,
    autoJoinBot: typeof input.autoJoinBot === 'boolean' ? input.autoJoinBot : DEFAULTS.autoJoinBot,
    autoJoinExcluded: sanitizeEventIds(input.autoJoinExcluded),
    autoJoinIncluded: sanitizeEventIds(input.autoJoinIncluded),
    uiLanguage: isAppLanguage(input.uiLanguage) ? input.uiLanguage : DEFAULTS.uiLanguage,
    summaryLanguage: isSummaryLanguage(input.summaryLanguage) ? input.summaryLanguage : DEFAULTS.summaryLanguage,
    summaryStyle: sanitizeSummaryStyle(input.summaryStyle),
    transcriptTerms: sanitizeTranscriptTerms(input.transcriptTerms),
    theme: isAppTheme(input.theme) ? input.theme : DEFAULTS.theme,
    behavior: sanitizeBehavior(input.behavior),
    meetings: sanitizeMeetings(input.meetings),
    summaryEmailEveryone:
      typeof input.summaryEmailEveryone === 'boolean'
        ? input.summaryEmailEveryone
        : DEFAULTS.summaryEmailEveryone,
    googleCalendarIds: sanitizeCalendarIds(input.googleCalendarIds),
    onboardedUsers: sanitizeUserIds(input.onboardedUsers),
    legalAccepted: sanitizeLegalAccepted(input.legalAccepted),
  };
}

/** Aynı bilgisayarda birkaç hesap açılabilir; liste sınırsız büyümesin. */
const MAX_REMEMBERED_USERS = 20;
const USER_ID_PATTERN = /^[0-9a-f-]{36}$/i;

function sanitizeUserIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids = value.filter((id): id is string => typeof id === 'string' && USER_ID_PATTERN.test(id));
  return [...new Set(ids)].slice(-MAX_REMEMBERED_USERS);
}

function sanitizeLegalAccepted(value: unknown): LegalAcceptance[] {
  if (!Array.isArray(value)) return [];
  const result: LegalAcceptance[] = [];
  for (const item of value) {
    const entry = (item ?? {}) as Partial<LegalAcceptance>;
    if (typeof entry.userId !== 'string' || !USER_ID_PATTERN.test(entry.userId)) continue;
    const versions: Record<string, string> = {};
    for (const [kind, version] of Object.entries(entry.versions ?? {})) {
      if (/^[a-z]{1,20}$/.test(kind) && typeof version === 'string' && version.length <= 40) versions[kind] = version;
    }
    result.push({ userId: entry.userId, versions });
  }
  return result.slice(-MAX_REMEMBERED_USERS);
}

/** Test edilebilir olsun diye dışa açık — ayar dosyasının temizlenmiş hâli. */
export function sanitizeSettings(value: unknown): AppSettings {
  return sanitize(value);
}

/** Özet e-postası tercihleri — yetki gerektirmez (bkz. EmailPrefs). */
export async function getEmailSettings(): Promise<EmailPrefs> {
  const { summaryEmailEnabled, summaryEmailEveryone } = await getSettings();
  return { summaryEmailEnabled, summaryEmailEveryone };
}

export async function saveEmailSettings(patch: unknown): Promise<EmailPrefs> {
  const input = (patch ?? {}) as Partial<EmailPrefs>;
  const next: Partial<AppSettings> = {};

  if (typeof input.summaryEmailEnabled === 'boolean') {
    next.summaryEmailEnabled = input.summaryEmailEnabled;
  }
  if (typeof input.summaryEmailEveryone === 'boolean') {
    next.summaryEmailEveryone = input.summaryEmailEveryone;
  }

  const saved = await saveSettings(next);
  return {
    summaryEmailEnabled: saved.summaryEmailEnabled,
    summaryEmailEveryone: saved.summaryEmailEveryone,
  };
}

/** Yapay zekâ özetlerinin dili — ai.ts her özet üretiminde bunu okur. */
export async function getSummaryLanguage(): Promise<SummaryLanguage> {
  return (await getSettings()).summaryLanguage;
}

/** Özet stili (kurallar + şablon) — ai.ts her özet üretiminde bunu okur. */
export async function getSummaryStyle(): Promise<SummaryStylePrefs> {
  return (await getSettings()).summaryStyle;
}

/** İsimler ve terimler — Deepgram isteklerine ve özet istemine ekleniyor. */
export async function getTranscriptTerms(): Promise<string[]> {
  return (await getSettings()).transcriptTerms;
}

/**
 * Ayarlar ekranının okuduğu kişisel tercihler. Yönetici kapısı yok: tema,
 * özet dili ve davranış anahtarları kullanıcının kendi tercihleri.
 */
export async function getAppPrefs(): Promise<AppPrefs> {
  const { summaryLanguage, summaryStyle, transcriptTerms, theme, behavior, meetings } = await getSettings();
  return { summaryLanguage, summaryStyle, transcriptTerms, theme, behavior, meetings };
}

export async function saveAppPrefs(patch: unknown): Promise<AppPrefs> {
  const input = (patch ?? {}) as Partial<AppPrefs>;
  const next: Partial<AppSettings> = {};

  if (isSummaryLanguage(input.summaryLanguage)) next.summaryLanguage = input.summaryLanguage;
  if (input.summaryStyle) next.summaryStyle = sanitizeSummaryStyle(input.summaryStyle);
  if (input.transcriptTerms) next.transcriptTerms = sanitizeTranscriptTerms(input.transcriptTerms);
  if (isAppTheme(input.theme)) next.theme = input.theme;
  if (input.behavior) {
    // Kısmi gönderim destekleniyor: yalnızca değişen anahtar gelebilir.
    const current = (await getSettings()).behavior;
    next.behavior = sanitizeBehavior({ ...current, ...input.behavior });
  }
  if (input.meetings) {
    const current = (await getSettings()).meetings;
    next.meetings = sanitizeMeetings({ ...current, ...input.meetings });
  }

  const saved = await saveSettings(next);
  return {
    summaryLanguage: saved.summaryLanguage,
    summaryStyle: saved.summaryStyle,
    transcriptTerms: saved.transcriptTerms,
    theme: saved.theme,
    behavior: saved.behavior,
    meetings: saved.meetings,
  };
}

/** Etkinlikleri okunan Google takvimleri; boşsa yalnızca ana takvim. */
export async function getGoogleCalendarIds(): Promise<string[]> {
  return (await getSettings()).googleCalendarIds;
}

export async function saveGoogleCalendarIds(ids: string[]): Promise<string[]> {
  return (await saveSettings({ googleCalendarIds: ids })).googleCalendarIds;
}

/** Arayüz dili — kişisel tercih, yönetici kapısı dışında. */
export async function getAppLanguage(): Promise<AppLanguage> {
  return (await getSettings()).uiLanguage;
}

export async function saveAppLanguage(language: unknown): Promise<AppLanguage> {
  if (!isAppLanguage(language)) throw new Error(t('Desteklenmeyen dil.', 'Unsupported language.'));
  return (await saveSettings({ uiLanguage: language })).uiLanguage;
}

const pickAutoJoin = ({ autoJoinBot, autoJoinExcluded, autoJoinIncluded }: AppSettings): AutoJoinPrefs => ({
  autoJoinBot,
  autoJoinExcluded,
  autoJoinIncluded,
});

export async function getAutoJoinPrefs(): Promise<AutoJoinPrefs> {
  return pickAutoJoin(await getSettings());
}

/** Genel ayar — toplantılarda elle yapılan seçimlere dokunmaz. */
export async function saveAutoJoinEnabled(enabled: unknown): Promise<AutoJoinPrefs> {
  return pickAutoJoin(await saveSettings({ autoJoinBot: enabled === true }));
}

/** Toplantının kendi bot düğmesi: açık → bot gitsin, kapalı → gitmesin (genel ayardan bağımsız). */
export async function setAutoJoinEvent(eventId: string, join: boolean): Promise<AutoJoinPrefs> {
  const current = await getSettings();
  const excluded = current.autoJoinExcluded.filter((id) => id !== eventId);
  const included = current.autoJoinIncluded.filter((id) => id !== eventId);
  (join ? included : excluded).push(eventId);
  return pickAutoJoin(await saveSettings({ autoJoinExcluded: excluded, autoJoinIncluded: included }));
}

/** Yönetici tanılama bilgisi (bkz. diagnostics.ts). */
export interface DiagnosticsInfo {
  appVersion: string;
  electronVersion: string;
  platform: string;
  envSource: string | null;
  recordingsPath: string;
  userDataPath: string;
  keys: Array<{ name: string; present: boolean; required: boolean }>;
}
